/* eslint-disable @typescript-eslint/no-explicit-any -- Service layer uses dynamic types for external API responses */
import { logger } from '@/utils/logger';
import { RequestBatcher } from '@/utils/RequestBatcher';
import { useStore } from '@/core/store';
import { AgentRunner, type AgentResponse } from './types';
import { TraceService } from './observability/TraceService';

export type TaskPriority = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface MaestroBatchResult {
    success: boolean;
    message?: string;
    error?: string;
    data: { taskId: string; thoughtSignature?: string };
}

export function toMaestroBatchResult(result: AgentResponse, taskId: string): MaestroBatchResult {
    const error = result.error || result.failure?.code;
    return {
        success: !error,
        message: result.text,
        ...(error ? { error } : {}),
        data: { taskId, thoughtSignature: result.thoughtSignature }
    };
}

export function batchTaskUpdateFromResult(result: MaestroBatchResult): {
    status: 'completed' | 'error';
    result: MaestroBatchResult;
    error?: string;
} {
    return result.success
        ? { status: 'completed', result }
        : { status: 'error', result, error: result.error || 'Agent did not report success.' };
}

export interface BatchedTask {
    id: string;
    description: string;
    /** Full instruction sent to the agent. Falls back to description for legacy callers. */
    prompt?: string;
    agentId: string;
    priority: TaskPriority;
    params: any;
    context?: any;
    traceId?: string;
    status: 'pending' | 'processing' | 'completed' | 'error';
    result?: any;
    error?: string;
}

export class MaestroBatchingService {
    private batcher: RequestBatcher<BatchedTask, MaestroBatchResult>;

    constructor() {
        this.batcher = new RequestBatcher(
            (tasks) => this.processBatch(tasks),
            { maxBatchSize: 5, maxWaitMs: 300 }
        );
    }

    /**
     * Orchestrate a task execution via the batcher.
     */
    async executeTask(task: Omit<BatchedTask, 'id' | 'status'>): Promise<MaestroBatchResult> {
        const id = crypto.randomUUID();
        const fullTask: BatchedTask = {
            ...task,
            id,
            status: 'pending'
        };

        // Add to global state for UI visibility
        useStore.getState().addBatchTask(fullTask);

        // Recursive Delegation Guard
        if (task.traceId) {
            const depth = await TraceService.getTraceDepth(task.traceId);
            if (depth >= TraceService.MAX_DELEGATION_DEPTH) {
                const errorMsg = `[Maestro] Delegation depth limit (${TraceService.MAX_DELEGATION_DEPTH}) reached or ancestry could not be verified for task ${id}.`;
                logger.warn(errorMsg);
                useStore.getState().updateBatchTask(id, { status: 'error', error: errorMsg });
                throw new Error(errorMsg);
            }
        }

        try {
            const result = await this.batcher.add(fullTask);
            useStore.getState().updateBatchTask(id, batchTaskUpdateFromResult(result));
            return result;
        } catch (error: unknown) {
            useStore.getState().updateBatchTask(id, { status: 'error', error: error instanceof Error ? error.message : String(error) });
            throw error;
        }
    }

    /**
     * Processes a collected batch of tasks.
     * Sorts by priority before execution.
     */
    private async processBatch(tasks: BatchedTask[]): Promise<MaestroBatchResult[]> {
        // 1. Sort by priority: URGENT (0) > HIGH (1) > MEDIUM (2) > LOW (3)
        const priorityMap: Record<TaskPriority, number> = {
            'URGENT': 0,
            'HIGH': 1,
            'MEDIUM': 2,
            'LOW': 3
        };

        const sortedTasks = [...tasks].sort((a, b) =>
            priorityMap[a.priority] - priorityMap[b.priority]
        );

        // Update status to processing in state
        sortedTasks.forEach(t => {
            useStore.getState().updateBatchTask(t.id, { status: 'processing' });
        });

        // 2. Execute tasks
        // Execute in parallel but store results mapped by ID so we return them in the exact
        // original `tasks` request order. This satisfies RequestBatcher's index assumption.
        const resultsMap = new Map<string, MaestroBatchResult>();
        await Promise.all(sortedTasks.map(async (t) => {
            const result = await this.executeIndividualTask(t);
            resultsMap.set(t.id, result);
        }));

        return tasks.map(t => resultsMap.get(t.id)!);
    }

    private agentRunner: AgentRunner | null = null;

    /**
     * Injects the agent runner function to avoid circular dependencies.
     */
    setRunner(runner: AgentRunner): void {
        this.agentRunner = runner;
    }

    private async executeIndividualTask(task: BatchedTask): Promise<MaestroBatchResult> {
        logger.debug(`[Maestro] Executing task ${task.id} for agent ${task.agentId} (Priority: ${task.priority})`);

        try {
            if (!this.agentRunner) {
                throw new Error('[Maestro] Agent runner not initialized');
            }

            const result = await this.agentRunner(
                task.agentId,
                task.prompt || task.description,
                task.context,
                task.traceId
            );

            return toMaestroBatchResult(result, task.id);
        } catch (error: unknown) {
            logger.error(`[Maestro] Task ${task.id} failed:`, error);
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error),
                data: { taskId: task.id }
            };
        }
    }

    /**
     * Orchestrate multiple tasks as a single logical batch.
     */
    async executeBatch(tasks: Omit<BatchedTask, 'id' | 'status'>[]): Promise<MaestroBatchResult[]> {
        return Promise.all(tasks.map(t => this.executeTask(t)));
    }
}

export const maestroBatchingService = new MaestroBatchingService();
